import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";

import { formatSalesImportFileSize } from "../utils/salesImportDraftModel";

export default function SalesImportFilePanel({
  file,
  busy = false,
  error = "",
  onSelect,
  onRemove,
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  function openSelector() {
    if (!busy) inputRef.current?.click();
  }

  function deliverFile(nextFile) {
    if (!busy && nextFile) onSelect?.(nextFile);
  }

  return (
    <Stack spacing={1.5}>
      <Typography component="h2" sx={{ color: "primary.900", fontSize: 17, fontWeight: 800 }}>
        Archivo
      </Typography>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        aria-label="Archivo Excel de ventas"
        disabled={busy}
        onChange={(event) => {
          deliverFile(event.target.files?.[0]);
          event.target.value = "";
        }}
        style={{ display: "none" }}
      />

      {file ? (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", justifyContent: "space-between", minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "neutral.700", fontSize: 11, fontWeight: 700 }}>
              Archivo seleccionado
            </Typography>
            <Typography
              title={file.name}
              sx={{
                color: "primary.900",
                fontSize: 15,
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name}
            </Typography>
            <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 650 }}>
              {formatSalesImportFileSize(file.size)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Tooltip title="Reemplazar archivo" arrow>
              <span>
                <IconButton
                  type="button"
                  aria-label="Reemplazar archivo"
                  disabled={busy}
                  onClick={openSelector}
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: "primary.500",
                    boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                    "&:hover": { bgcolor: "primary.600" },
                  }}
                >
                  <UploadFileIcon sx={{ color: "logoblanco", fontSize: 19 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Quitar archivo" arrow>
              <span>
                <IconButton
                  type="button"
                  aria-label="Quitar archivo"
                  disabled={busy}
                  onClick={onRemove}
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: "secondary.500",
                    boxShadow: "0px 4px 12px rgba(76, 72, 141, 0.2)",
                    "&:hover": { bgcolor: "secondary.600" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ color: "logoblanco", fontSize: 19 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      ) : (
        <Box
          role="group"
          aria-label="Seleccionar o arrastrar archivo de ventas"
          aria-disabled={busy}
          onClick={openSelector}
          onDragEnter={(event) => {
            event.preventDefault();
            if (!busy) setDragActive(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragActive(false);
            deliverFile(event.dataTransfer.files?.[0]);
          }}
          sx={{
            minHeight: 280,
            p: 2,
            borderRadius: "22px",
            border: "2px dashed",
            borderColor: dragActive ? "primary.500" : "secondary.300",
            bgcolor: dragActive ? "secondary.100" : "neutral.100",
            boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.22)",
            cursor: busy ? "wait" : "pointer",
          }}
        >
          <Stack spacing={1.2} sx={{ height: "100%", justifyContent: "center" }}>
            <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}>
              Arrastrá el archivo hasta esta área o seleccionalo desde tu equipo.
            </Typography>
            <Button
              type="button"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                openSelector();
              }}
              endIcon={<UploadFileIcon />}
              sx={{
                width: "100%",
                height: 38,
                borderRadius: "10px",
                bgcolor: "primary.500",
                color: "logoblanco",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 800,
                boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                "&:hover": { bgcolor: "primary.600" },
              }}
            >
              Seleccionar archivo
            </Button>
            <Typography sx={{ color: "neutral.700", fontSize: 11, fontWeight: 600 }}>
              Formato permitido: .xlsx · Tamaño máximo: 10 MB
            </Typography>
          </Stack>
        </Box>
      )}

      {error ? (
        <Box role="alert" sx={{ px: 1.5, py: 1.1, borderRadius: "14px", bgcolor: "secondary.700" }}>
          <Typography sx={{ color: "logoblanco", fontSize: 12, fontWeight: 700 }}>
            {error}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}
